import { useCallback, useMemo } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import type { Permission } from "@/shared/types/permission.types";

/**
 * Shared permission helper for the "Access & permissions" sections — centralizes the staff-admin shortcut
 * (`isInternalUser && isAdminUser`) that every section previously repeated inline.
 *
 * NOTE ON AUTHORITY: `can()` reads the legacy `scopes[]` from `/auth/me`, which the backend keeps only for
 * backward compatibility — the guards authorize by RBAC roles → policies (deny-first), with no admin bypass
 * (internal admins pass because they carry `full_admin_access`). So this hook gates edit _affordances_
 * optimistically; the authoritative signal remains the backend 403, surfaced as `hasNoAccess` by the data
 * hooks. A hidden button is a UX nicety, never the security boundary.
 */
export function useAccessCan() {
  const { can, isInternalUser, isAdminUser } = useAuth();

  const isStaffAdmin = isInternalUser && isAdminUser;

  /** True when the user is a staff admin or holds EVERY listed scope. */
  const canAll = useCallback(
    (...scopes: Permission[]) => isStaffAdmin || scopes.every((scope) => can(scope)),
    [isStaffAdmin, can],
  );

  return useMemo(() => ({ isStaffAdmin, canAll }), [isStaffAdmin, canAll]);
}
